import { delay, takeEvery } from 'redux-saga';
import {
	call, fork, put, select, take as takeEffect,
} from 'redux-saga/effects';
import { actions as apiActions, selectors as apiSelectors } from 'zender-api-client';
import map from 'lodash/map';
import values from 'lodash/values';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import difference from 'lodash/difference';
import get from 'lodash/get';
import sampleSize from 'lodash/sampleSize';
import sample from 'lodash/sample';
import take from 'lodash/take';
import takeRight from 'lodash/takeRight';
import random from 'lodash/random';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import * as appSelectors from '../selectors';

import options from '../ui/interaction/children/animatedVisualizations/options';
import { submission } from '../actions';
import logger from '../log';

const {
	SUBMISSION_RESET, SUBMISSION_SELECT, SUBMISSION_SET_DATA, SUBMISSION_TOGGLE,
} = submission;

const getSelected = state => get(state, 'submission.selected', []);

const getFocussed = state => get(state, 'submission.focussed');

const getData = state => get(state, 'submission.data', []);

const getStarredSubmissionIdsFromState = function* getStarredSubmissionIdsFromState(challengeId) {
	const challenges = yield select(apiSelectors.ugc.challengesSelector);
	const challenge = challenges[challengeId] || {};
	if (challenge) {
		const submissions = values(challenge.submissions);
		if (submissions.length) {
			return map(filter(submissions, sub => sub.stars && sub.stars === 5), 'id');
		}
		console.warn(`No submissions found for challenge: ${challengeId}`);
	}
	return [];
};

const streamReadySaga = function* streamReady() {
	const challengeId = yield select(appSelectors.router.challengeIdSelector);
	if (challengeId) {
		yield put(apiActions.ugc.uiActions.getStarredSubmissions(challengeId, 5));
	} else {
		yield put(apiActions.shoutbox.uiActions.getStarredShouts(5));
	}
};

const challengeUpdateSaga = function* challengeUpdateSaga(action) {
	const status = get(action, 'payload.data.status');
	const challengeId = yield select(appSelectors.router.challengeIdSelector);
	const updateChallengeId = get(action, 'payload.data.id');
	if (status === 'started' && challengeId === updateChallengeId) {
		yield put(apiActions.ugc.uiActions.getStarredSubmissions(challengeId, 5));
	}
};

const starredSubmissionsSaga = function* starredSubmissions(action) {
	const challengeId = get(action, 'payload.request.challengeId');
	const data = yield call(getStarredSubmissionIdsFromState, challengeId);
	if (data) {
		yield put({ type: SUBMISSION_SET_DATA, payload: data });
		const selected = sampleSize(data, options.maxSelectedCount);
		yield put({ type: SUBMISSION_SELECT, payload: uniq(selected) });
	}
};

const starredShoutsSaga = function* starredShoutsGenerator(action) {
	const starredShouts = yield select(apiSelectors.shoutbox.starredShoutsSelector);
	const selected = yield select(getSelected);
	if (starredShouts.length) {
		const data = map(starredShouts, 'id');
		yield put({ type: SUBMISSION_SET_DATA, payload: data });
		if (selected.length === 0) {
			const payload = sampleSize(data, options.maxSelectedCount);
			yield put({ type: SUBMISSION_SELECT, payload: uniq(payload) });
		} else if (selected.length < options.maxSelectedCount) {
			const diff = difference(data, selected);
			const extra = sample(diff);
			if (extra) {
				const payload = selected.concat(extra);
				yield put({ type: SUBMISSION_SELECT, payload: uniq(payload) });
			}
		}
	} else {
		console.warn('No shouts found for stream');
	}
};

const shoutTriggerSaga = function* shoutTriggerSaga(action) {
	const shoutId = get(action, 'payload.data.shout.id');
	const data = yield select(getData);
	if (shoutId && !includes(data, shoutId)) {
		yield put({ type: SUBMISSION_SET_DATA, payload: data.concat(shoutId) });
	}
	const focussed = yield select(getFocussed);
	if (focussed === shoutId) {
		yield put({ type: SUBMISSION_RESET });
	} else {
		yield put({ type: SUBMISSION_TOGGLE, payload: shoutId });
	}
};

const submissionUpdateSaga = function* submissionUpdate(action) {
	const challengeId = get(action, 'payload.data.challengeId');

	const challengeIdUrl = yield select(appSelectors.router.challengeIdSelector);
	if (challengeId !== challengeIdUrl) {
		return;
	}

	const data = yield call(getStarredSubmissionIdsFromState, challengeId);
	yield put({ type: SUBMISSION_SET_DATA, payload: data });
	const stars = get(action, 'payload.data.submission.stars');
	const state = get(action, 'payload.data.submission.state');
	const submissionId = get(action, 'payload.data.submission.id');
	const focussed = yield select(getFocussed);
	const selected = yield select(getSelected);

	if (submissionId) {
		if (stars === 5) {
			if (state === 'focussed') {
				yield put({ type: SUBMISSION_TOGGLE, payload: submissionId });
			} else if (state === 'unfocussed' && submissionId === focussed) {
				yield put({ type: SUBMISSION_RESET });
			}
		} else if (stars === 0) {
			if (submissionId === 'focussed') {
				yield put({ type: SUBMISSION_RESET });
			}
			const dataIds = yield select(getData);
			const removeFromSelected = difference(selected, dataIds);
			yield put({ type: SUBMISSION_SELECT, payload: uniq(without(selected, removeFromSelected, submissionId)) });
		}
	}
};

const submissionDeleteSaga = function* submissionDelete(action) {
	const challengeId = get(action, 'payload.data.challengeId');
	const submissionId = get(action, 'payload.data.submission.id');
	const data = yield call(getStarredSubmissionIdsFromState, challengeId);
	yield put({ type: SUBMISSION_SET_DATA, payload: data });
	const focussed = yield select(getFocussed);
	const selected = yield select(getSelected);
	if (includes(selected, submissionId)) {
		yield put({ type: SUBMISSION_SELECT, payload: uniq(difference(selected, submissionId)) });
	}
	if (focussed === submissionId) {
		yield put({ type: SUBMISSION_RESET });
	}
};

const switchRandomSaga = function* switchRandom() {
	const focussed = yield select(getFocussed);
	const data = yield select(getData);
	const selected = yield select(getSelected);
	const diff = difference(data, selected);
	const randomItem = sample(diff);
	if (!focussed) {
		if (randomItem) {
			// now just switch a random item in selected with randomItem while maintaining order
			const randIdx = Math.floor(random(1.0, options.maxSelectedCount));
			const left = take(selected, randIdx - 1).concat(randomItem);
			const right = takeRight(selected, (options.maxSelectedCount - randIdx));
			const payload = left.concat(right);
			yield put({ type: SUBMISSION_SELECT, payload: uniq(payload) });
		}
		const challengeId = yield select(appSelectors.router.challengeIdSelector);
		if (!challengeId) {
			yield put(apiActions.shoutbox.uiActions.getStarredShouts(5));
		}
	}
};

const starredMessagesListener = () => function* starredMessages() {
	while (true) {
		yield take(apiActions.stream.uiActionTypes.READY);
		yield put(apiActions.shoutbox.uiActions.getStarredShouts(5));
	}
};

const streamReadyListener = function* streamReadyListener() {
	yield* takeEvery(apiActions.stream.uiActionTypes.READY, streamReadySaga);
};

const challengeUpdateListener = function* challengeUpdateListener() {
	yield* takeEvery(apiActions.ugc.pushActionTypes.CHALLENGE_UPDATE, challengeUpdateSaga);
};

const starredSubmissionsListener = function* starredSubmissionsListener() {
	yield* takeEvery(apiActions.ugc.apiActionTypes.GET_STARRED_SUBMISSIONS.SUCCESS, starredSubmissionsSaga);
};

const starredShoutsListener = function* starredShoutsListener() {
	yield* takeEvery(apiActions.shoutbox.apiActionTypes.GET_STARRED_SHOUTS.SUCCESS, starredShoutsSaga);
};

const triggerShoutListener = function* triggerShoutListener() {
	yield* takeEvery(apiActions.shoutbox.pushActionTypes.SHOUT_TRIGGER, shoutTriggerSaga);
};

const submissionUpdateListener = function* submissionUpdateListener() {
	yield* takeEvery(apiActions.ugc.pushActionTypes.SUBMISSION_UPDATE, submissionUpdateSaga);
};

const submissionDeleteListener = function* submissionDeleteListener() {
	yield* takeEvery(apiActions.ugc.pushActionTypes.SUBMISSION_DELETE, submissionDeleteSaga);
};

const switchRandomListener = function* switchRandomListener() {
	yield takeEffect(submission.SUBMISSION_INIT);
	yield call(delay, options.randomSwitchInterval);
	while (true) {
		yield call(switchRandomSaga);
		yield call(delay, options.randomSwitchInterval);
	}
};

const pusherLog = function pusherLog(action) {
	logger.info({ tag: 'pusher', events: action.payload });
};

const pusherLogListener = function* pusherLogListener() {
	yield* takeEvery(apiActions.client.pushActionTypes.PUSHER_LOG, pusherLog);
};

export default [
	fork(streamReadyListener),
	fork(challengeUpdateListener),
	fork(starredSubmissionsListener),
	fork(starredShoutsListener),
	fork(triggerShoutListener),
	fork(submissionUpdateListener),
	fork(submissionDeleteListener),
	fork(switchRandomListener),
	fork(pusherLogListener),
	fork(starredMessagesListener),
];



// WEBPACK FOOTER //
// ./src/sagas/submission.js